// TODO: Resolve eslint errors
/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */

import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { GoogleDriveFile } from '@server/modules/data-room/common/external-storage-integration/google-drive/google-drive';
import { TRPCClientError } from '@trpc/client';
import { FileWithPath, fromEvent } from 'file-selector';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DropEvent, useDropzone } from 'react-dropzone';
import { useNavigate } from 'react-router-dom';
import { useFilePicker } from 'use-file-picker';

import {
  CopyIcon,
  ExitIcon,
  FileIconWhite,
  FolderIcon,
  GoogleDriveIcon,
  GreenCheckmarkIcon,
} from '@/assets/icons';
import { UploadIcon } from '@/assets/images';
import { MAX_FILE_SIZE, MAX_FILE_SIZE_MB, MAX_INT_32 } from '@/common/constants';
import { FileValidationResult } from '@/common/types';
import { ACCEPTED_EXTENSIONS, ACCEPTED_TYPES, FILE_UPLOAD_ACCEPT_OBJECT } from '@/common/utils';
import LoadingSpinner from '@/components/LoadingSpinner';
import { useNewDiligenceWizard } from '@/contexts/create-new-diligence-wizard/utils';
import { MarveriButton } from '@/pages/home/common/MarveriButton';
import { DocumentSetUploaderList } from '@/pages/home/content/DocumentSetUploaderList';
import { IntegrationService } from '@/pages/home/content/integration/common';
import {
  MICROSOFT_FILE_PICKER_PREFIX,
  msalConfig,
  MSFT_FILE_PICKER_CANCEL,
  MSFT_FILE_PICKER_ERROR,
  MSFT_FILE_PICKER_PENDING,
  MSFT_FILE_PICKER_SUCCESS,
} from '@/pages/home/content/integration/microsoft/MSALConfig';
import { MicrosoftAccount, MicrosoftFile } from '@/pages/home/content/integration/microsoft/types';
import { MarveriIcon } from '@/pages/overview/common/MarveriIcon';
import { ScrollableDiv } from '@/pages/overview/common/ScrollableDiv';
import { copyToClipboard } from '@/utils/clipboard';
import { trpcReact } from '@/utils/trpc';

interface DocumentSetUploaderProps {
  onUploadDone: (files: File[]) => void;
  onFileDelete: (fileToDelete: File) => void;
  totalCount: number;
}

export const DocumentSetUploader = ({
  onUploadDone,
  onFileDelete,
  totalCount,
}: DocumentSetUploaderProps) => {
  const MAX_FILES = 1000 - totalCount; // TODO: remove magic number

  const navigate = useNavigate();
  const { newDiligenceData, updateNewDiligenceData } = useNewDiligenceWizard();
  const { dataRoomFiles } = newDiligenceData;
  const folderInputRef = useRef<HTMLInputElement | null>(null);
  const [dragging, setDragging] = useState(false);
  const [rejectedFiles, setRejectedFiles] = useState<FileValidationResult[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<FileWithPath[] | undefined>(dataRoomFiles);

  /**
   * Common Functions for file upload
   */
  const formatClientName = (date: Date) => {
    const options: Intl.DateTimeFormatOptions = {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    };

    return date.toLocaleDateString('en-US', options);
  };

  const validateFile = (file: FileWithPath): FileValidationResult => {
    if (file.size > MAX_FILE_SIZE) {
      return { file, valid: false, reason: `File too large (${MAX_FILE_SIZE_MB}MB limit)` };
    }
    if (!file.size) {
      return { file, valid: false, reason: 'Empty file - try unzipping before dragging' };
    }
    if (!ACCEPTED_TYPES.includes(file.type)) {
      return { file, valid: false, reason: 'Wrong file type' };
    }
    return { file, valid: true };
  };

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const filesToUpload = [...(uploadedFiles || []), ...(acceptedFiles || [])];
      setDragging(false);
      setUploadedFiles((prevFiles) => {
        const files = Array.isArray(prevFiles) ? prevFiles : [];
        return [...files, ...acceptedFiles];
      });
      onUploadDone(acceptedFiles);
      const dateInMS = Date.now() % MAX_INT_32;
      updateNewDiligenceData({
        client: {
          name: formatClientName(new Date()),
          number: dateInMS,
        },
        name: formatClientName(new Date()),
        number: dateInMS,
        dataRoomFiles: filesToUpload,
        selectedTools: [...(newDiligenceData?.selectedTools || [])],
      });
    },
    [newDiligenceData?.selectedTools, onUploadDone, updateNewDiligenceData, uploadedFiles],
  );

  const getFilesOrFolders = async (event: DropEvent) => {
    const raw_files = await fromEvent(event);
    const files = raw_files.filter((file) => (file as FileWithPath).path !== undefined);
    const validatedFiles = files
      .slice(0, MAX_FILES * 10)
      .map((file) => validateFile(file as FileWithPath));

    const validFiles = validatedFiles.filter((result) => result.valid).map((result) => result.file);
    const rejectedFilesWithReasons = validatedFiles.filter((result) => !result.valid);

    setRejectedFiles((prevRejectedFiles) => [...prevRejectedFiles, ...rejectedFilesWithReasons]);
    return validFiles.slice(0, MAX_FILES);
  };

  const processFiles = useCallback(
    (files: FileWithPath[], folderFiles?: boolean) => {
      const validatedFiles = files.slice(0, MAX_FILES * 10).map((file) => validateFile(file));

      const validFiles = validatedFiles
        .filter((result) => result.valid)
        .map((result) => result.file);
      const rejectedFilesWithReasons = validatedFiles.filter((result) => !result.valid);
      setRejectedFiles((prevRejectedFiles) => [...prevRejectedFiles, ...rejectedFilesWithReasons]);

      const acceptedFiles = validFiles.slice(0, MAX_FILES);

      setUploadedFiles((prevFiles) => {
        const files = Array.isArray(prevFiles) ? prevFiles : [];
        return [...files, ...acceptedFiles];
      });

      const foldersWithFiles = Array.from(acceptedFiles || []).map((file) => {
        const fullPath = '/' + (file.webkitRelativePath || file.name);
        return new File([file], fullPath, { type: file.type });
      });

      const filesToUpload = [...(uploadedFiles || []), ...(acceptedFiles || [])];
      const foldersToUpload = [...(uploadedFiles || []), ...(foldersWithFiles || [])];

      onUploadDone(folderFiles ? foldersToUpload : filesToUpload);
      const dateInMS = Date.now() % MAX_INT_32;
      updateNewDiligenceData({
        client: {
          name: formatClientName(new Date()),
          number: dateInMS,
        },
        name: formatClientName(new Date()),
        number: dateInMS,
        dataRoomFiles: folderFiles ? foldersToUpload : filesToUpload,
        selectedTools: [...(newDiligenceData?.selectedTools || [])],
      });
    },
    [
      MAX_FILES,
      newDiligenceData?.selectedTools,
      onUploadDone,
      updateNewDiligenceData,
      uploadedFiles,
    ],
  );

  const { openFilePicker } = useFilePicker({
    readAs: 'DataURL',
    accept: ACCEPTED_TYPES,
    multiple: true,
    onFilesSuccessfullySelected: async ({ plainFiles }) => {
      const files = plainFiles.filter((file) => (file as FileWithPath).path !== undefined);
      processFiles(files);
    },
  });

  const handleFolderUploadClick = () => {
    folderInputRef.current?.click();
  };

  const onFolderUpload = useCallback(
    (selectedFolder: FileList | null) => {
      if (!selectedFolder) return;
      const files = Array.from(selectedFolder).filter(
        (file) => (file as FileWithPath).webkitRelativePath !== undefined,
      );

      processFiles(files, true);
    },
    [processFiles],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxFiles: MAX_FILES,
    accept: FILE_UPLOAD_ACCEPT_OBJECT,
    getFilesFromEvent: (event) => getFilesOrFolders(event),
    noClick: true,
    noKeyboard: true,
    onDragLeave: () => setDragging(false),
  });

  const dragOverListener = useCallback(() => {
    setDragging(true);
  }, []);

  const fileFormatString = useMemo(() => {
    return ACCEPTED_EXTENSIONS.join(', ');
  }, []);

  /**
   * Integration
   */
  // Allowed integrations are determined by the Feature Flag
  // Update LaunchDarkly to include new integrations and update INTEGRATION_DISPLAY_NAMES to match
  const { allowedIntegrations } = useFlags();
  const enableIntegration: boolean = Boolean(allowedIntegrations?.length);
  const INTEGRATION_DISPLAY_NAMES: Record<string, string> = {
    'google-drive': 'Google Drive',
    'microsoft-sharepoint': 'Microsoft Sharepoint',
    'microsoft-onedrive': 'Microsoft OneDrive',
  };

  /**
   * State variables for external storage integration
   */
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Modal State
  type IntegrationStatus =
    | 'select-integration'
    | 'connect-integration'
    | 'import-google-files'
    | 'import-microsoft-files'
    | 'file-upload'
    | 'upload-success'
    | 'upload-error';

  const [currentStep, setCurrentStep] = useState<IntegrationStatus>('select-integration');
  const [selectedIntegration, setSelectedIntegration] = useState<string | null>(null);
  const [integrationConnected, setIntegrationConnected] = useState(false);
  // TODO: Add better error handling for integration errors
  // const [integrationError, setIntegrationError] = useState<string | null>(null);

  // Client Matter Management
  const createClientMatter = trpcReact.clientMatter.create.useMutation();

  // Google Drive Integration
  const [serviceAccountEmail, setServiceAccountEmail] = useState<string | null>(null);
  const createGoogleServiceAccount = trpcReact.dataRoom.createGoogleServiceAccount.useMutation();
  const listGoogleDriveFiles = trpcReact.dataRoom.listGoogleDriveFiles.useQuery(undefined, {
    enabled: false, // Disable automatic fetching
  });
  const importGoogleDriveFiles = trpcReact.dataRoom.importGoogleDriveFiles.useMutation();
  const [googleDriveFiles, setGoogleDriveFiles] = useState<GoogleDriveFile[]>([]);

  // Helper function to copy service account email to clipboard
  const handleCopy = () => {
    copyToClipboard(serviceAccountEmail || '', 'Copied to clipboard!');
  };

  // Microsoft Integration
  const [microsoftPopupInitiated, setMicrosoftPopupInitiated] = useState(false); // Track if popup has been initiated - main purpose is to prevent multiple calls to open the popup
  const microsoftPopupWindowRef = useRef<Window | null>(null);
  const messageListenerRef = useRef<((e: MessageEvent) => void) | null>(null);
  const [microsoftFiles, setMicrosoftFiles] = useState<MicrosoftFile[]>([]);
  const [microsoftAuthenticatedAccount, setMicrosoftAuthenticatedAccount] =
    useState<MicrosoftAccount | null>(null);
  const importMicrosoftFiles = trpcReact.dataRoom.importMicrosoftFiles.useMutation();

  /**
   * Close the Microsoft popup window and clean up (remove all associated state and event listeners)
   * This is the ONLY place where popup closing should happen to avoid duplicate cleanup
   */
  const closeMicrosoftPopup = useCallback(() => {
    console.log('[MSFT Integration] Closing popup and cleaning up');

    // Close the popup window
    if (microsoftPopupWindowRef.current && !microsoftPopupWindowRef.current.closed) {
      microsoftPopupWindowRef.current.close(); // For debugging, comment out to keep popup open
    }
    microsoftPopupWindowRef.current = null;

    // Clean up message listener
    if (messageListenerRef.current) {
      window.removeEventListener('message', messageListenerRef.current);
      messageListenerRef.current = null;
    }

    // Remove all session storage items related to Microsoft popup
    // Safe call - should have been removed by MSAL Auth by now
    sessionStorage.removeItem(MSFT_FILE_PICKER_PENDING);
    sessionStorage.removeItem('MARVERI_PARENT_ORIGIN');
  }, []);

  /**
   * Common functions for external storage integration
   */
  const resetIntegrationState = useCallback(() => {
    setCurrentStep('select-integration');
    setSelectedIntegration(null);
    setIntegrationConnected(false);

    // Google Drive Reset
    setServiceAccountEmail(null);
    setGoogleDriveFiles([]);

    // Microsoft Sharepoint/OneDrive Reset
    setMicrosoftFiles([]);
    setMicrosoftAuthenticatedAccount(null);
    closeMicrosoftPopup();
  }, [closeMicrosoftPopup]);

  /**
   * Common functions for external storage integration
   */
  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    resetIntegrationState();
  }, [resetIntegrationState]);

  const handleIntegrationSelect = async (integration: string) => {
    if (uploadedFiles?.length && uploadedFiles.length > 0) return;

    setSelectedIntegration(integration);
    setIsModalOpen(true);
    setCurrentStep('connect-integration');

    switch (integration) {
      case 'google-drive':
        try {
          const result = await createGoogleServiceAccount.mutateAsync();
          if (result && result.email) {
            setServiceAccountEmail(result.email);
          }
        } catch (error) {
          console.error('[GSA Integration] Error creating Google Service Account:', error);
        }
        setIntegrationConnected(true);
        break;
      case 'microsoft-sharepoint':
      case 'microsoft-onedrive':
        // Reset popup state when starting new integration
        setMicrosoftPopupInitiated(false);
        setIntegrationConnected(true);

        // Directly go to import files step to initiate MSAL and OneDrive file picker popup window
        setCurrentStep('import-microsoft-files');
        break;
      default:
        throw new Error(`[Integration] Unsupported integration: ${integration}`);
    }
  };

  /**
   * Google Drive Integration
   */
  const handleFetchGoogleDriveFiles = async () => {
    // Prepare for fetching files
    setLoading(true);
    setCurrentStep('import-google-files');

    try {
      const result = await listGoogleDriveFiles.refetch();

      if (result.data) {
        console.log('[GSA Integration] Received listGoogleDriveFiles response:', result.data);
        // TODO: add validation for result.data format (to be in GoogleDriveFile[])
        // Currently rely on the type inference from the backend
        setGoogleDriveFiles(result.data);
        console.log('[GSA Integration] Google Drive files:', googleDriveFiles);
      }
    } catch (error) {
      console.error('[GSA Integration] Error fetching Google Drive files:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleDriveUpload = async () => {
    setCurrentStep('file-upload');

    try {
      // Check pre-conditions
      if (!serviceAccountEmail) {
        throw new Error('Service account email is not set');
      }
      if (googleDriveFiles.length === 0) {
        throw new Error('No files to upload');
      }

      // Create a new client matter to store the Google Drive files
      const dateInMS = Date.now() % MAX_INT_32;
      const newClientMatter = await createClientMatter.mutateAsync({
        name: formatClientName(new Date()),
        number: dateInMS,
        client: {
          name: formatClientName(new Date()),
          number: dateInMS,
        },
        tools: [],
      });

      // Import the files
      // Prepare the files to import
      const filesToImport = googleDriveFiles.map((file) => ({
        id: file.id,
        mimeType: file.mimeType,
        name: file.name,
      }));
      console.log(`[GSA Integration] Sending request to import ${filesToImport.length} files`);
      await importGoogleDriveFiles.mutateAsync({
        serviceAccountEmail: serviceAccountEmail,
        files: filesToImport,
        clientMatterId: newClientMatter.id,
      });

      closeModal();
      navigate(`/${newClientMatter.id}/dataroom`);
    } catch (error) {
      console.error('[GSA Integraton] Error uploading files:', error);

      // TODO: Display error message to user if error occurs in the upload process
      // TBD untill UI design is finalized
    }
  };

  /**
   * Microsoft Integration (Sharepoint & OneDrive)
   */

  // Microsoft file picker function (Sharepoint and OneDrive)
  const openMsftFilePicker = useCallback(() => {
    // Prevent multiple calls to open the popup
    if (microsoftPopupInitiated) {
      console.log('[MSFT Integration] Waiting for current popup to finish, ignoring request');
      return;
    }

    // Check if popup window is still open
    if (microsoftPopupWindowRef.current && !microsoftPopupWindowRef.current.closed) {
      console.log('[MSFT Integration] Focusing existing popup window');
      microsoftPopupWindowRef.current.focus();
      return;
    }

    // Set the flags BEFORE opening popup to avoid race condition
    setMicrosoftPopupInitiated(true);
    sessionStorage.setItem(MSFT_FILE_PICKER_PENDING, 'true'); // to be shared with MSAL Auth
    console.log(
      `[MSFT Integration] Set session storage flag: ${sessionStorage.getItem(MSFT_FILE_PICKER_PENDING)}. Set microsoftPopupInitiated to ${microsoftPopupInitiated}.`,
    );

    // Open the popup window
    console.log('[MSFT Integration] Opening OneDrive/Sharepoint file picker popup');
    const popup = window.open(
      msalConfig.auth.redirectUri,
      'msal-auth-popup', // Use fixed name to avoid conflicts with other popups
      'width=800,height=700,scrollbars=yes,resizable=yes,location=no,menubar=no,toolbar=no',
    );

    if (!popup) {
      console.error('[MSFT Integration] Popup blocked by browser');
      resetIntegrationState();
      return;
    } else {
      console.log('[MSFT Integration] Popup opened successfully');
      // Set the popup window reference
      microsoftPopupWindowRef.current = popup;
      popup.focus();
    }
  }, [microsoftPopupInitiated, resetIntegrationState]);

  useEffect(() => {
    if (
      currentStep === 'import-microsoft-files' &&
      !microsoftPopupInitiated &&
      !microsoftPopupWindowRef.current
    ) {
      // Add small delay to ensure UI is ready
      const timeoutId = setTimeout(() => {
        openMsftFilePicker();
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [currentStep, microsoftPopupInitiated, openMsftFilePicker]);

  // Handle messages from the Microsoft popup
  useEffect(() => {
    // Clean up previous listener if it exists
    if (messageListenerRef.current) {
      window.removeEventListener('message', messageListenerRef.current);
    }

    const onMessage = (e: MessageEvent) => {
      if (typeof e.data !== 'string') return;

      try {
        const message = JSON.parse(e.data);
        if (!message?.type?.startsWith(MICROSOFT_FILE_PICKER_PREFIX)) return;

        if (message.type === MSFT_FILE_PICKER_SUCCESS) {
          console.log(
            '[MSFT Integration] Sharepoint/OneDrive picker success',
            JSON.stringify(message),
          );

          const { selectedFiles, email, accessToken } = message;
          if (!selectedFiles) {
            console.error('[MSFT Integration] No files selected');
            resetIntegrationState();
            return;
          }
          if (!email || !accessToken) {
            console.error(
              '[MSFT Integration] Authenticated account information missing. User needs to re-authenticate',
            );
            resetIntegrationState();
            return;
          }

          // Close popup immediately after successful selection
          closeMicrosoftPopup();

          // Update state variables
          setMicrosoftAuthenticatedAccount({
            email,
            accessToken,
          });
          setMicrosoftFiles(selectedFiles);
          console.log(
            `[MSFT Integration] Authenticated account: ${microsoftAuthenticatedAccount} selected ${selectedFiles.length} files`,
          );
        } else if (message.type === MSFT_FILE_PICKER_CANCEL) {
          console.log('[MSFT Integration] User cancelled file selection');
          resetIntegrationState();
        } else if (message.type === MSFT_FILE_PICKER_ERROR) {
          console.error('[MSFT Integration] File picker error:', message.error);
          resetIntegrationState();
        }
      } catch (error) {
        console.error('[MSFT Integration] Invalid message format:', error);
        return;
      }
    };

    messageListenerRef.current = onMessage;
    window.addEventListener('message', onMessage);

    return () => {
      if (messageListenerRef.current) {
        window.removeEventListener('message', messageListenerRef.current);
        messageListenerRef.current = null;
      }
    };
  }, [
    selectedIntegration,
    microsoftAuthenticatedAccount,
    closeMicrosoftPopup,
    resetIntegrationState,
  ]);

  const handleMicrosoftUpload = useCallback(async () => {
    try {
      if (microsoftFiles.length === 0)
        throw new TRPCClientError('[MSFT Integration] No files to upload');

      if (!microsoftAuthenticatedAccount)
        throw new TRPCClientError('[MSFT Integration] No authenticated account');

      // 1. Create a new client matter to store the Microsoft files
      const dateInMS = Date.now() % MAX_INT_32;
      const newClientMatter = await createClientMatter.mutateAsync({
        name: formatClientName(new Date()),
        number: dateInMS,
        client: {
          name: formatClientName(new Date()),
          number: dateInMS,
        },
        tools: [],
      });

      // 2. Import the files

      console.log(
        `[Microsoft Integration] Sending request to import ${microsoftFiles.length} files`,
      );
      await importMicrosoftFiles.mutateAsync({
        service:
          selectedIntegration === 'microsoft-sharepoint'
            ? IntegrationService.MICROSOFT_SHAREPOINT
            : IntegrationService.MICROSOFT_ONEDRIVE,
        account: microsoftAuthenticatedAccount,
        files: microsoftFiles,
        clientMatterId: newClientMatter.id,
      });

      closeModal();
      navigate(`/${newClientMatter.id}/dataroom`);
    } catch (error) {
      console.error('[Microsoft Integration] Error uploading files:', error);

      // TODO: Display error message to user if error occurs in the upload process
      // TBD untill UI design is finalized
    }
  }, [
    selectedIntegration,
    createClientMatter,
    microsoftAuthenticatedAccount,
    microsoftFiles,
    importMicrosoftFiles,
    closeModal,
    navigate,
  ]);

  // NOTE: UI for integration is not complete
  // TODO: Remove placeholder UI and implement proper UI UX for integration
  return (
    <div className="relative mb-[24px] flex w-full flex-col" data-testid="dataroom-uploader">
      <div className="flex items-baseline justify-between">
        <h1 className="text-[20px] font-medium leading-[30px] text-marveri-white">Upload Files</h1>
        <div>
          {uploadedFiles === undefined ||
          (uploadedFiles.length === 0 && rejectedFiles.length === 0) ? (
            <div
              className="text-center text-[14px] font-medium text-marveri-light-silver"
              data-testid="dataroom-uploader-no-docs"
            >
              No files uploaded
            </div>
          ) : (
            <div className="flex">
              <h1 className="font-medium text-[#868686]">{`${uploadedFiles?.length} File${
                uploadedFiles?.length > 1 ? `s` : ''
              } Successfully Uploaded`}</h1>
              {rejectedFiles && rejectedFiles.length !== 0 && (
                <>
                  <span className="px-[5px] font-medium text-marveri-white">{`/`}</span>
                  <h1 className="font-medium text-marveri-red">{`${rejectedFiles.length} File${
                    rejectedFiles.length > 1 ? 's' : ''
                  } Rejected`}</h1>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="flex w-full">
        <div className={`${enableIntegration && 'flex-col'} flex w-full gap-2 pt-[16px]`}>
          {totalCount + rejectedFiles.length === 0 || isDragActive || dragging ? (
            <div
              {...getRootProps()}
              className={`relative flex min-h-[260px] w-full cursor-not-allowed flex-col items-center justify-center rounded-lg border border-dashed border-dark-border shadow small:h-[180px] small:min-h-[200px] ${
                isDragActive ? 'border-blue-500 bg-container-selected' : 'bg-[#19181A] '
              }`}
            >
              <form>
                <input {...getInputProps()} />
              </form>
              <div className="flex flex-col items-center justify-center py-[10px]">
                <div className="flex flex-col items-center text-center">
                  <img src={UploadIcon} alt="icon" />
                  <p className="p-2 pb-[7px] text-[18px] font-bold text-marveri-white">
                    Drag & Drop Files or Folders
                  </p>
                  <div>
                    <div>
                      <p className="text-[12px] font-normal text-N400">
                        Supported file formats: <br />{' '}
                        <span className="font-semibold">{fileFormatString}</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div onDragOver={dragOverListener} className="w-full">
              <DocumentSetUploaderList
                onFileDelete={onFileDelete}
                uploadedFiles={uploadedFiles}
                setUploadedFiles={setUploadedFiles}
                rejectedFiles={rejectedFiles}
                setRejectedFiles={setRejectedFiles}
              />
            </div>
          )}
          {enableIntegration ? (
            <>
              <div className="my-4 flex items-center justify-center gap-4">
                <span className="text-[12px] font-medium text-N400">OR</span>
              </div>
              <div className="flex justify-center gap-4 small:h-[180px]">
                <div
                  className={`flex h-[56px] w-[240px] cursor-pointer items-center justify-center gap-2 rounded-[12px] bg-[#242324] shadow hover:bg-[#353336] small:h-[180px] ${
                    !enableIntegration ? 'h-1/2' : 'h-1/3'
                  }`}
                  onClick={openFilePicker}
                >
                  <h1>File Upload</h1>
                </div>
                <div
                  className={`flex h-[56px] w-[240px] cursor-pointer items-center justify-center gap-2 rounded-[12px] bg-[#242324] shadow hover:bg-[#353336] small:h-[180px] ${
                    !enableIntegration ? 'h-1/2' : 'h-1/3'
                  }`}
                  onClick={handleFolderUploadClick}
                >
                  <h1>Folder Upload</h1>
                </div>
                {enableIntegration && (
                  <>
                    <div
                      className={`${uploadedFiles && uploadedFiles.length > 0 ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-[#353336]'} flex h-[56px] w-[240px] items-center justify-center gap-2 rounded-[12px] bg-[#242324] shadow small:h-[180px]`}
                      onClick={() => handleIntegrationSelect('google-drive')}
                    >
                      <h1>Import from Drive</h1>
                    </div>
                    <div
                      className={`${uploadedFiles && uploadedFiles.length > 0 ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-[#353336]'} flex h-[56px] w-[240px] items-center justify-center gap-2 rounded-[12px] bg-[#242324] shadow small:h-[180px]`}
                      onClick={() => handleIntegrationSelect('microsoft-sharepoint')}
                    >
                      <h1>Import from SharePoint</h1>
                    </div>
                    <div
                      className={`${uploadedFiles && uploadedFiles.length > 0 ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-[#353336]'} flex h-[56px] w-[240px] items-center justify-center gap-2 rounded-[12px] bg-[#242324] shadow small:h-[180px]`}
                      onClick={() => handleIntegrationSelect('microsoft-onedrive')}
                    >
                      <h1>Import from OneDrive</h1>
                    </div>
                  </>
                )}
                <input
                  type="file"
                  id="folderInput"
                  ref={folderInputRef}
                  style={{ display: 'none' }}
                  multiple
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    onFolderUpload(event.target.files)
                  }
                  {...({ webkitdirectory: 'true' } as { webkitdirectory: string })}
                />
              </div>
            </>
          ) : (
            <div className="flex flex-col justify-between gap-2">
              <div
                className={`flex w-[160px] cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-dark-border bg-[#19181A] shadow hover:border-solid hover:bg-container-selected ${
                  !enableIntegration ? 'h-1/2' : 'h-1/3'
                }`}
                onClick={openFilePicker}
              >
                <img src={FileIconWhite} alt="file-icon" />
                <h1>File Upload</h1>
              </div>
              <div
                className={`flex w-[160px] cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-dark-border bg-[#19181A] shadow hover:border-solid hover:bg-container-selected ${
                  !enableIntegration ? 'h-1/2' : 'h-1/3'
                }`}
                onClick={handleFolderUploadClick}
              >
                <img src={FolderIcon} alt="folder-icon" />
                <h1>Folder Upload</h1>
              </div>
              <input
                type="file"
                id="folderInput"
                ref={folderInputRef}
                style={{ display: 'none' }}
                multiple
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  onFolderUpload(event.target.files)
                }
                {...({ webkitdirectory: 'true' } as { webkitdirectory: string })}
              />
            </div>
          )}
        </div>
      </div>
      {isModalOpen && (
        <Dialog
          open={isModalOpen}
          onClose={closeModal}
          className="fixed inset-0 z-10 flex items-center justify-center overflow-y-auto bg-black/50"
        >
          <DialogPanel className="relative flex min-h-[300px] w-[656px] flex-col justify-center rounded-[16px] bg-[#292829] p-8 text-marveri-white">
            {currentStep !== 'connect-integration' && currentStep !== 'file-upload' && !loading && (
              <MarveriIcon
                icon={ExitIcon}
                onClick={closeModal}
                iconType="other"
                iconStyle="absolute right-8"
              />
            )}
            {currentStep === 'connect-integration' &&
            selectedIntegration === 'google-drive' &&
            integrationConnected ? (
              <>
                <div className="mt-6 flex flex-col items-center">
                  {serviceAccountEmail ? (
                    <div className="">
                      <DialogTitle className="mb-8 text-left text-[24px] font-medium leading-6">
                        Import from {INTEGRATION_DISPLAY_NAMES[selectedIntegration ?? '']}
                      </DialogTitle>
                      <p className="mb-6">
                        Please share the desired folder with the following e-mail. Once you&apos;ve
                        shared folders, click &quot;Next&quot;.
                      </p>
                      <div className="mb-8 flex gap-2">
                        <div className="flex items-center rounded-[4px] bg-[#242324] px-4 py-[10px]">
                          <p className="break-all text-sm">{serviceAccountEmail}</p>
                        </div>
                        <div
                          className="group relative flex size-10 cursor-pointer items-center justify-center rounded-[4px] bg-[#242324] hover:opacity-80 focus:outline-none"
                          onClick={handleCopy}
                        >
                          <img src={CopyIcon} alt="Copy" className="size-5" />
                          <div className="absolute right-0 top-0 z-10 hidden h-[25px] w-[120px] translate-x-[40px] translate-y-[-30px] items-start justify-center gap-1 rounded-lg border border-[#d9d9d9] bg-white p-1.5 shadow after:absolute after:left-1/2 after:top-full after:size-0 after:-translate-x-1/2 after:border-8 after:border-solid after:border-transparent after:border-t-white after:drop-shadow-[0_1px_0_rgba(217,217,217,1)] after:content-[''] group-hover:inline-block">
                            <div className="text-center font-['Inter'] text-xs font-medium leading-none text-black">
                              Copy to clipboard
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="mt-8 flex justify-end gap-2">
                        <div
                          onClick={closeModal}
                          className="flex h-[44px] w-[68px] cursor-pointer items-center justify-center rounded-[28px] px-[22px] py-[16px] text-N200 hover:text-[#EAE6EA]"
                        >
                          Close
                        </div>
                        {serviceAccountEmail && (
                          <MarveriButton
                            disabled={false}
                            onClick={handleFetchGoogleDriveFiles}
                            className="w-[68px]"
                          >
                            Next
                          </MarveriButton>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-8">
                      <LoadingSpinner size={20} />
                      <p>Loading service account details ...</p>
                    </div>
                  )}
                </div>
              </>
            ) : currentStep === 'import-google-files' ? (
              <>
                <div className="mt-6 flex flex-col items-center">
                  {loading ? (
                    <div className="flex flex-col items-center gap-8">
                      <LoadingSpinner size={20} />
                      <p>Fetching files shared with {serviceAccountEmail} ...</p>
                    </div>
                  ) : (
                    <div className="flex w-full flex-col">
                      <DialogTitle className="mb-8 text-left text-[24px] font-medium leading-6">
                        Import from {INTEGRATION_DISPLAY_NAMES[selectedIntegration ?? '']}
                      </DialogTitle>
                      {googleDriveFiles.length > 0 ? (
                        <div className="w-full">
                          <span className="mb-2 flex justify-end text-[14px] font-medium text-N400">
                            {googleDriveFiles.length} file{googleDriveFiles.length > 1 ? 's' : ''}{' '}
                            queued
                          </span>
                          <div className="rounded-[12px] bg-[#19181A] p-2">
                            <ScrollableDiv
                              containerStyle="max-h-[450px] h-[calc(100vh-300px)] pr-2"
                              scrollKey="uploader-google-drive-files"
                            >
                              {googleDriveFiles.map((file, index) => (
                                <div
                                  key={index}
                                  className="mb-2 flex w-full rounded-[12px] border border-N600 bg-[#242324] py-3 pl-3 pr-4"
                                >
                                  <div className="flex w-full items-center gap-[14px]">
                                    <div className="flex size-8 items-center justify-center rounded-[28px] bg-light-border">
                                      <img src={FileIconWhite} className="size-[16px] " />
                                    </div>
                                    <p>{file.name}</p>
                                    <img src={GreenCheckmarkIcon} className="size-[20px] " />
                                  </div>
                                  <img src={GoogleDriveIcon} />
                                </div>
                              ))}
                            </ScrollableDiv>
                          </div>
                          <div className="mt-8 flex justify-end gap-2 text-[14px]">
                            <div
                              onClick={closeModal}
                              className="flex h-[44px] w-[68px] cursor-pointer items-center justify-center rounded-[28px] px-[22px] py-[16px] text-N200 hover:text-[#EAE6EA]"
                            >
                              Close
                            </div>

                            <MarveriButton
                              disabled={googleDriveFiles.length === 0}
                              onClick={handleGoogleDriveUpload}
                            >
                              Create Project
                            </MarveriButton>
                          </div>
                        </div>
                      ) : (
                        <>
                          <p className="mb-4">
                            No files found. Check if files are successfully shared with{' '}
                            {serviceAccountEmail}. If issue persists, close the wizard and try
                            agian.
                          </p>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </>
            ) : currentStep === 'import-microsoft-files' ? (
              <>
                <div>
                  {microsoftFiles.length > 0 ? (
                    <div className="w-full">
                      <span className="mb-2 flex justify-end text-[14px] font-medium text-N400">
                        {microsoftFiles.length} file{microsoftFiles.length > 1 ? 's' : ''} queued
                      </span>
                      <div className="rounded-[12px] bg-[#19181A] p-2">
                        <ScrollableDiv
                          containerStyle="max-h-[450px] h-[calc(100vh-300px)] pr-2"
                          scrollKey="uploader-microsoft-files"
                        >
                          {microsoftFiles.map((file, index) => (
                            <div
                              key={index}
                              className="mb-2 flex w-full rounded-[12px] border border-N600 bg-[#242324] py-3 pl-3 pr-4"
                            >
                              <div className="flex w-full items-center gap-[14px]">
                                <div className="flex size-8 items-center justify-center rounded-[28px] bg-light-border">
                                  <img src={FileIconWhite} className="size-[16px] " />
                                </div>
                                <p>{file.name ?? file.id}</p>
                                <img src={GreenCheckmarkIcon} className="size-[20px] " />
                              </div>
                            </div>
                          ))}
                        </ScrollableDiv>
                      </div>
                      <div className="mt-8 flex justify-end gap-2 text-[14px]">
                        <div
                          onClick={closeModal}
                          className="flex h-[44px] w-[68px] cursor-pointer items-center justify-center rounded-[28px] px-[22px] py-[16px] text-N200 hover:text-[#EAE6EA]"
                        >
                          Close
                        </div>

                        <MarveriButton
                          disabled={microsoftFiles.length === 0}
                          onClick={handleMicrosoftUpload}
                        >
                          Create Project
                        </MarveriButton>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-4">
                      {microsoftPopupInitiated ? (
                        <>
                          <p>
                            Please use the popup window to select files from{' '}
                            {INTEGRATION_DISPLAY_NAMES[selectedIntegration ?? '']}.
                          </p>
                          <p className="text-sm text-N400">
                            If the popup window doesn&apos;t appear, check if popups are blocked in
                            your browser.
                          </p>
                          <MarveriButton
                            disabled={false}
                            onClick={() => {
                              resetIntegrationState();
                            }}
                            className="mt-4"
                          >
                            Cancel
                          </MarveriButton>
                        </>
                      ) : (
                        <>
                          <p>Something went wrong. Please try again.</p>
                          <MarveriButton
                            disabled={false}
                            onClick={() => {
                              resetIntegrationState();
                            }}
                          >
                            Cancel
                          </MarveriButton>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </>
            ) : currentStep === 'file-upload' ? (
              <div className="flex flex-col items-center gap-8">
                <div className="flex justify-center">
                  <LoadingSpinner size={20} />
                </div>
                <p className="text-[20px] font-medium text-marveri-white">Uploading files...</p>
              </div>
            ) : (
              <div>
                <p>External Integration Wizard</p>
              </div>
            )}
          </DialogPanel>
        </Dialog>
      )}
    </div>
  );
};
